import { DataSourceArray } from 'photoswipe';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import { ReactPortal, useState } from 'react';
import * as sanity from '../../../sanity-image';

import { SanityFigure } from '../../../../graphql-types';
import { getDimensionsFromSanityImage } from '../../../helpers/get-dimensions';

type Props = {
    images: SanityFigure[];
    onOpen?: () => void;
    onClose?: () => void;
};

export const useLightbox = ({ images, onOpen, onClose }: Props) => {
    const [contentPortal, setContentPortal] = useState<ReactPortal | null>(null);

    const dataSource = imagesToDataSource(images);

    const lightbox = new PhotoSwipeLightbox({
        pswpModule: () => import('photoswipe'),
        mainClass: '!bg-neutral-900/60',
    });

    lightbox.on('uiRegister', function () {
        lightbox.pswp?.ui?.registerElement({
            name: 'custom-caption',
            order: 9,
            className:
                'bg-neutral-900 text-white text-sm w-[calc(100%-32px)] max-w-[400px] px-2 py-1 rounded absolute left-1/2 bottom-4 transform -translate-x-1/2',
            isButton: false,
            appendTo: 'root',
            onInit: (el, pswp) => {
                pswp.on('change', () => {
                    const caption = pswp.currSlide?.data?.caption as string;
                    el.innerHTML = caption ?? '';
                    el.classList.toggle('invisible', !caption);
                });
            },
        });
    });

    lightbox.addFilter('thumbEl', (thumbEl, data) => {
        if (data.thumbEl) {
            return data.thumbEl as HTMLElement;
        }

        return thumbEl as HTMLElement;
    });

    lightbox.addFilter('placeholderSrc', (placeholderSrc, slide) => {
        return slide.data.src ?? placeholderSrc;
    });

    if (typeof window !== 'undefined') {
        lightbox.init();
    }

    lightbox.on('close', () => {
        if (typeof onClose === 'function') {
            onClose();
        }

        setContentPortal(null);
    });

    const open = (index: number, thumbElement: HTMLElement | null = null) => {
        if (thumbElement) {
            dataSource[index].thumbEl = thumbElement;
        }

        lightbox.loadAndOpen(index, dataSource);

        if (typeof onOpen === 'function') {
            onOpen();
        }
    };

    return {
        open,
        contentPortal,
    };
};

function imagesToDataSource(images: SanityFigure[]): DataSourceArray {
    return images
        .map(image => {
            if (!image || !image.asset?.url) {
                return null;
            }

            const { width, height } = getDimensionsFromSanityImage(
                image.asset.metadata?.dimensions?.width ?? 0,
                image.asset.metadata?.dimensions?.height ?? 0,
                image.crop
            );

            return {
                src: sanity.urlFor(image).url() || '',
                width,
                height,
                alt: image.altText,
                caption: image.caption,
                thumbCropped: true,
            };
        })
        .filter(Boolean) as DataSourceArray;
}
